import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TrackByFunction,
} from '@angular/core';
import { DayTileComponent } from '../day-tile/day-tile.component';
import { RewardModalComponent } from '../reward-modal/reward-modal.component';
import {
  CalendarDay,
  CalendarModalState,
  CalendarPlayResponse,
} from '../../models/calendar.models';
import { CoreService } from 'src/app/core/helpers/core.service';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/core/helpers/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { DataServiceService } from 'src/app/core/helpers/data-service.service';
import { GaService } from 'src/app/core/helpers/ga.service';

function assertDateString(value: string): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    throw new Error(`Invalid ISO date string: ${value}`);
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function toUtcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

function parseIsoDate(value: string): Date {
  const [year, month, day] = assertDateString(value);
  return toUtcDate(year, month, day);
}

function todayUtc(): Date {
  const now = new Date();
  return toUtcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
}

function addUtcDays(date: Date, amount: number): Date {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + amount);
  return next;
}

function toIsoString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

@Component({
  selector: 'loyaltic-advent-calendar',
  standalone: false,
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnChanges {
  @Input() startDate?: string;
  @Input() endDate?: string;
  @Input() playedDates: string[] = [];

  @Output() boostedChange = new EventEmitter<boolean>();

  days: CalendarDay[] = [];
  loading = false;
  modal: CalendarModalState = {
    open: false,
    loading: false,
  };
  selectedDate?: string;
  isLoggedIn = false;
  showModal = false;
  isAnimationLoading = true;

  private boostTimeout?: ReturnType<typeof setTimeout>;
  private continuousBoost = false;
  private sub!: Subscription;
  videoAnimationLink = '';
  showWinComponent = false;


  constructor(
    private coreService: CoreService,
    private detectChanges: ChangeDetectorRef,
    private authService: AuthService,
    private translate: TranslateService,
    private dataService: DataServiceService,
    private cdr: ChangeDetectorRef,
    private gaService: GaService
  ) {

  }

  ngOnInit(): void {
    this.sub = this.coreService.isLoggedIn$.subscribe(
      (status) => {
        this.isLoggedIn = status;
        this.detectChanges.detectChanges();
    });
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate'] || changes['playedDates']) {
      this.generateCalendar();
    }
  }

  trackByDate: TrackByFunction<CalendarDay> = (_index, day) => day.date;

  onPlay(day: CalendarDay): void {
    if (day.state !== 'active') {
      return;
    }
    this.isAnimationLoading = true;
    this.detectChanges.detectChanges();
    let participateApiResult = JSON.parse(localStorage.getItem('participateApiResult'));
    const gameId = this.getFirstUnplayedGameIdByDate(participateApiResult, day.date);
    const userId = this.authService.getUserId();
    this.coreService.play(userId, 0, gameId).subscribe((res:any) => {
      this.coreService.videoAnimationLink = res.result.mobileVideoName;
      this.dataService.winDetails = res.result;
      this.gaService.emitEvent(res.result.isRewardDeliverable ? 'Deliverable' : 'Not deliverable', 'Play Screen');
      this.cdr.detectChanges();
      this.showModal = true;
      this.pulseBoost();
      this.loading = false;
      const result = this.createMockResult(day, res.result.hasWon, res.result);
      this.modal = {
        open: true,
        loading: false,
        result: { ...result, date: day.date },
      };

      this.selectedDate = day.date;
    }, (error:any) => {
      
    })
  }

  getFirstUnplayedGameIdByDate(slots: any, targetDate: string): string | null {
    // Normalize input date for comparison
    
    const formattedTarget = new Date(targetDate).toDateString();
    // Find the slot matching the criteria
    const slot = slots.slotsInfo.find(s =>
      new Date(s.date).toDateString() === formattedTarget &&
      !s.isPlayed &&
      s.slotStatus !== 'COMPLETED'
    );

    if (!slot) return null;

    // Find the first unplayed game in that slot
    const unplayedGame = slot.games.find(g => !g.isPlayed);
    return unplayedGame ? unplayedGame.gameId : null;
  }

  get calendarRows(): number[] {
  return Array.from(
    { length: Math.ceil(this.days.length / 4) },
    (_, i) => i
  );
}

  onHover(active: boolean): void {
    if (active) {
      //this.startContinuousBoost();
    } else {
      this.stopContinuousBoost();
    }
  }

  onModalClose(hasWon: boolean) {
    this.modal = {
      open: false,
      loading: false,
    };
    this.showWinComponent = hasWon;
  }

  private pulseBoost(duration = 1500): void {
    this.clearBoostTimeout();
    this.boostedChange.emit(true);
    this.boostTimeout = setTimeout(() => {
      this.boostTimeout = undefined;
      if (!this.continuousBoost) {
        this.boostedChange.emit(false);
      }
    }, duration);
  }

  private startContinuousBoost(): void {
    this.continuousBoost = true;
    this.clearBoostTimeout();
    this.boostedChange.emit(true);
  }

  private stopContinuousBoost(): void {
    this.continuousBoost = false;
    this.clearBoostTimeout();
    this.boostedChange.emit(false);
  }

  private clearBoostTimeout(): void {
    if (this.boostTimeout) {
      clearTimeout(this.boostTimeout);
      this.boostTimeout = undefined;
    }
  }

  private generateCalendar(): void {
    this.loading = true;

    const today = todayUtc();
    const [start, end] = this.resolveRange(today);
    const played = new Set(this.playedDates);

    const days: CalendarDay[] = [];
    let cursor = start;
    let firstActive: string | undefined;
    const todayIso = toIsoString(today);
    let preferredSelection: string | undefined;

    while (cursor.getTime() <= end.getTime()) {
      const isoDate = toIsoString(cursor);
      const isToday = isoDate === todayIso;
      const isPastOrToday = cursor.getTime() <= today.getTime();

      let state: CalendarDay['state'] = 'locked';
      if (played.has(isoDate) && isPastOrToday) {
        state = 'played';
      } else if (isPastOrToday) {
        state = 'active';
      }

      if (state === 'active') {
        if (!firstActive) {
          firstActive = isoDate;
        }
        if (isToday) {
          preferredSelection = isoDate;
        }
      }

      days.push({
        date: isoDate,
        label: cursor.getUTCDate().toString(),
        state,
        span: this.weightedSpan(),
        isToday,
      });

      cursor = addUtcDays(cursor, 1);
    }

    this.days = days;
    this.selectedDate = preferredSelection ?? firstActive;
    this.loading = false;
  }

  private resolveRange(today: Date): [Date, Date] {
    if (this.startDate && this.endDate) {
      return [parseIsoDate(this.startDate), parseIsoDate(this.endDate)];
    }

    const start = toUtcDate(today.getUTCFullYear(), today.getUTCMonth() + 1, 1);
    const end = addUtcDays(start, 23);
    return [start, end];
  }

  private shuffleDays(days: CalendarDay[]): CalendarDay[] {
    const shuffled = [...days];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private weightedSpan(): CalendarDay['span'] {
    return '1x1';
  }

  returnSlotCount(targetDate: string): number {
    if (this.isLoggedIn) {
      let participateApiResult = JSON.parse(localStorage.getItem('participateApiResult'));
      // Normalize the target date (ignore time differences)
      const formattedTarget = new Date(targetDate).toDateString();

      // Find the slot matching the given date
      const slot = participateApiResult.slotsInfo.find(s => new Date(s.date).toDateString() === formattedTarget);
      // Return true if the slot exists and status is "COMPLETED"
      return slot.games.filter((game:any) => !game.isPlayed).length-1;
    }
  }

  private createMockResult(day: CalendarDay, won:boolean, response:any): CalendarPlayResponse {
    if (won) {
      return {
        won: true,
        gamesRemaining: this.returnSlotCount(day.date),
        reward: {
          id: `reward-${day.date}`,
          title: `${response.reward}`,
          description: response.rewardDescription,
          ctaText: 'Claim',
          ctaUrl: '#',
          image: response.rewardImage,
        },
      };
    }

    return {
      won: false,
      gamesRemaining: this.returnSlotCount(day.date),
      message: this.translate.instant('pop-no-win-info'),
    };
  }
}
